import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import ReactMarkdown from "../../components/ReactMarkdown";
import airtable from "../../lib/airtables";
import { ArtistAmbassador, ArtistAmbassadorEvent } from "../../lib/airtables/models";
import { teacherFieldIds } from "../../lib/airtables/tables/Teachers";
import { artistAmbassadorEventFieldIds } from "../../lib/airtables/tables/ArtistAmbassadorEvents";
import { artistAmbassadorFieldIds } from "../../lib/airtables/tables/ArtistAmbassadors";

// Events with this many registrations are full and no longer selectable.
const MAX_EVENT_CAPACITY = 4;

const formatEventDateTime = (isoDateTime?: string) => {
  if (!isoDateTime) return "Unknown time";
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return "Unknown time";

  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
};

const ArtistBio = ({ bio }: { bio?: string }) => {
  const [expanded, setExpanded] = useState(false);

  if (!bio) return null;
  const isLong = bio.length > 280;
  const shown = expanded || !isLong ? bio : `${bio.slice(0, 280).trimEnd()}…`;

  return (
    <div className="mt-2 text-sm text-gray-600 whitespace-pre-line">
      {shown}
      {isLong ? (
        <button
          type="button"
          className="ml-1 font-medium token-link-primary text-[var(--color-action-primary)]"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      ) : null}
    </div>
  );
};

export default function ArtistAmbassadorEventsPage() {
  const { user } = useAuth();
  const [artists, setArtists] = useState<ArtistAmbassador[]>([]);
  const [events, setEvents] = useState<ArtistAmbassadorEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [savedEventId, setSavedEventId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const isWinner = Boolean(user?.[teacherFieldIds.artistAmbassadorWinner]);

  useEffect(() => {
    if (!user || !isWinner) {
      setLoading(false);
      return;
    }

    Promise.all([
      airtable.artistAmbassadors.getOpenForBooking(),
      airtable.artistAmbassadorEvents.getUpcoming(),
    ])
      .then(([artistList, eventList]) => {
        setArtists(artistList);
        setEvents(eventList);

        // Preselect the teacher's existing registration, if any
        const registered = user[teacherFieldIds.artistAmbassadorEvents] as string[] | undefined;
        const existing = registered?.find((id) =>
          eventList.some((event) => event.id === id)
        );
        if (existing) {
          setSelectedEventId(existing);
          setSavedEventId(existing);
        }
      })
      .catch((error) => {
        console.error("Failed to load artist ambassador events", error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [user, isWinner]);

  // Hide full events, except the one the teacher is already registered for
  // (so their current selection stays visible even when it filled up).
  const availableEvents = useMemo(
    () =>
      events.filter(
        (event) =>
          (event[artistAmbassadorEventFieldIds.numRegistrations] ?? 0) < MAX_EVENT_CAPACITY ||
          event.id === savedEventId
      ),
    [events, savedEventId]
  );

  const eventsByArtist = useMemo(() => {
    const grouped: Record<string, ArtistAmbassadorEvent[]> = {};
    availableEvents.forEach((event) => {
      (event[artistAmbassadorEventFieldIds.artist] ?? []).forEach((artistId: string) => {
        if (!grouped[artistId]) grouped[artistId] = [];
        grouped[artistId].push(event);
      });
    });
    return grouped;
  }, [availableEvents]);

  const bookableArtists = useMemo(
    () => artists.filter((artist) => (eventsByArtist[artist.id ?? ""] ?? []).length > 0),
    [artists, eventsByArtist]
  );

  const saveSelection = async () => {
    if (!user?.id || !selectedEventId || isSaving) return;

    setIsSaving(true);
    setStatusMessage("");
    try {
      // Re-check capacity right before saving — the count updates live as
      // other teachers register, and the page may have been open a while.
      if (selectedEventId !== savedEventId) {
        const latest = await airtable.artistAmbassadorEvents.select(selectedEventId);
        if (latest && (latest[artistAmbassadorEventFieldIds.numRegistrations] ?? 0) >= MAX_EVENT_CAPACITY) {
          setEvents((prev) =>
            prev.map((event) => (event.id === latest.id ? { ...event, ...latest } : event))
          );
          setSelectedEventId(savedEventId);
          setStatusMessage(
            "Sorry, that time just filled up — please choose another available time."
          );
          setIsSaving(false);
          return;
        }
      }

      await airtable.teachers.update(user.id, {
        [teacherFieldIds.artistAmbassadorEvents]: [selectedEventId],
      });
      setSavedEventId(selectedEventId);
      setStatusMessage(
        "Your workshop selection has been saved. We look forward to seeing your classroom there!"
      );
    } catch (error) {
      console.error("Failed to save workshop selection", error);
      setStatusMessage("Something went wrong saving your selection — please try again.");
    }
    setIsSaving(false);
  };

  if (!user) {
    return (
      <main className="page bg-white">
        <div className="max-w-3xl mx-auto py-16 px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Artist Ambassador Workshops</h1>
          <p className="text-gray-600 mb-6">
            Please log in to view available workshops and select the artist, date and time for
            your classroom.
          </p>
          <Link
            to="/login"
            className="inline-block rounded-lg bg-[var(--color-action-primary)] px-6 py-3 font-medium text-white"
          >
            Log in
          </Link>
        </div>
      </main>
    );
  }

  if (!isWinner) {
    return (
      <main className="page bg-white">
        <div className="max-w-3xl mx-auto py-16 px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Artist Ambassador Workshops</h1>
          <p className="text-gray-600 mb-6">
            Workshop selection is available to classrooms that have been selected to
            participate in an Artist Ambassador Workshop. If your classroom is selected,
            you'll see an invitation on your dashboard.
          </p>
          <Link
            to="/app/dashboard"
            className="inline-block rounded-lg bg-[var(--color-action-primary)] px-6 py-3 font-medium text-white"
          >
            Back to dashboard
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page bg-white">
      <div className="max-w-5xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Artist Ambassador Workshops</h1>
        <p className="text-gray-600 mb-8">
          Select the artist, date and time you wish to participate in, then confirm your
          selection below.
        </p>

        {loading ? (
          <p className="text-gray-500">Loading available workshops…</p>
        ) : loadError ? (
          <p className="text-red-600">
            We couldn't load the available workshops right now — please refresh to try again.
          </p>
        ) : bookableArtists.length === 0 ? (
          <p className="text-gray-500">
            There are no workshops open for booking at the moment — please check back soon!
          </p>
        ) : (
          <>
            <div className="space-y-8">
              {bookableArtists.map((artist) => {
                const photoUrl =
                  artist[artistAmbassadorFieldIds.photo]?.[0]?.thumbnails?.large?.url ?? artist[artistAmbassadorFieldIds.photo]?.[0]?.url;
                const artistEvents = eventsByArtist[artist.id ?? ""] ?? [];

                return (
                  <article
                    key={artist.id}
                    className="rounded-2xl border border-gray-200 bg-gray-50 p-6 shadow-sm"
                  >
                    <div className="flex flex-col gap-5 sm:flex-row">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={artist[artistAmbassadorFieldIds.name]}
                          className="h-36 w-36 flex-shrink-0 rounded-xl object-cover"
                        />
                      ) : null}
                      <div className="min-w-0">
                        <h2 className="text-2xl font-semibold text-gray-800">{artist[artistAmbassadorFieldIds.name]}</h2>
                        <p className="text-sm font-medium text-[var(--color-action-primary)]">
                          {artist[artistAmbassadorFieldIds.professionCraft]}
                          {artist.Location ? ` · ${artist.Location}` : ""}
                        </p>
                        <ArtistBio bio={artist[artistAmbassadorFieldIds.bio]} />
                      </div>
                    </div>

                    {artist[artistAmbassadorFieldIds.workshopDescription]?.trim() ? (
                      <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
                        <p className="mb-2 text-sm font-semibold text-gray-700">
                          Workshop Description
                        </p>
                        <ReactMarkdown className="space-y-2 text-sm text-gray-600">
                          {artist[artistAmbassadorFieldIds.workshopDescription]}
                        </ReactMarkdown>
                      </div>
                    ) : null}

                    <fieldset className="mt-5 border-t border-gray-200 pt-4">
                      <legend className="sr-only">{`Available times with ${artist[artistAmbassadorFieldIds.name]}`}</legend>
                      <p className="mb-3 text-sm font-semibold text-gray-700">Available times:</p>
                      <div className="space-y-2">
                        {artistEvents.map((event) => (
                          <label
                            key={event.id}
                            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                              selectedEventId === event.id
                                ? "border-[var(--color-action-primary)] bg-white"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name="artist-ambassador-event"
                              value={event.id}
                              checked={selectedEventId === event.id}
                              onChange={() => setSelectedEventId(event.id ?? "")}
                              className="h-4 w-4"
                            />
                            <span className="text-gray-800">
                              {formatEventDateTime(event[artistAmbassadorEventFieldIds.dateTime])}
                            </span>
                            {savedEventId && savedEventId === event.id ? (
                              <span className="ml-auto rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                                Your current selection
                              </span>
                            ) : null}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </article>
                );
              })}
            </div>

            <div className="sticky bottom-0 mt-8 border-t border-gray-200 bg-white py-4">
              {statusMessage ? (
                <p
                  className={`mb-3 text-sm ${
                    statusMessage.startsWith("Something") ? "text-red-600" : "text-green-700"
                  }`}
                >
                  {statusMessage}
                </p>
              ) : null}
              <button
                type="button"
                onClick={saveSelection}
                disabled={!selectedEventId || selectedEventId === savedEventId || isSaving}
                className="rounded-lg bg-[var(--color-action-primary)] px-6 py-3 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving
                  ? "Saving…"
                  : savedEventId
                  ? "Update my selection"
                  : "Confirm my selection"}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
